/* SPEC-563 — the per-user sidebar layout, held ONCE for the whole shell.
 *
 * One provider (in AppShell) feeds both Sidebar instances (desktop + phone overlay), the
 * rail's account menu and the "Arrange menu" dialog. Two independent copies would be two
 * tabs in one window: each would PUT its own blob and 409 the other.
 *
 * ── The favourites races (SPEC-452 comment block, kept verbatim) ──────────────────────
 * Server-persisted (GET on mount, PUT on every toggle) so the pins follow the
 * planner to any browser. Three races had to be closed, because the PUT replaces
 * the WHOLE list and the server is last-write-wins:
 *   1. Saves are SERIALISED through `saveChain`, so two quick stars cannot land
 *      out of order and leave the stored list behind the one on screen.
 *   2. Only the NEWEST save's response may touch state (`saveSeq`), so a slow
 *      earlier response cannot roll the nav back to a superseded list.
 *   3. `touched` stops a slow initial GET from wiping a pin the planner made
 *      while that GET was still in flight.
 * On failure the click-time list is restored — a star that stayed lit after a
 * failed save would be a lie, and the pin would be gone on the next load.
 *
 * ── How SPEC-563 closes each one, plus the fourth (another tab or device) ─────────────
 * The state is a CONFIRMED copy (what the server last said: layout + version) and a queue
 * of PENDING ops (pure functions from config/nav-layout.ts). What the menu shows is always
 * `pending.reduce(apply, confirmed)`.
 *   1. Saves are still serialised through `saveChain`; each flush sends confirmed + EVERY
 *      pending op as one blob with base_version = confirmed.version, so quick clicks
 *      coalesce and can never land out of order.
 *   2. Newest wins by construction: a response only replaces CONFIRMED and drops the ops it
 *      carried (`seq` marks them); ops made since stay queued and are re-applied on top,
 *      so a slow response can never roll the screen back.
 *   3. `touched` is subsumed: a flush waits for the initial GET (`loaded`), and the GET
 *      only sets CONFIRMED, so an op made while it was in flight is re-applied on top.
 *      If that GET FAILED, a flush reads again before it writes (`readOk`) and never
 *      saves on top of a menu it could not read — that would bury the legacy pins.
 *   4. Another tab saved first → 409 with the server's layout. Re-apply the pending ops on
 *      it and retry, up to MAX_RECONCILES times, each time on the layout the LATEST 409
 *      carried (saveWithReconcile, config/nav-layout.ts). If the ops change nothing on the
 *      newer layout, say so and send nothing. If it is still behind after the bound, show
 *      the newest layout, drop the ops and say so. The ops are intents, not toggles (the
 *      star is favouriteOp), so replaying one on a newer layout cannot undo the same change
 *      made in the other tab.
 * Any other failure drops the ops (the click is NOT applied — a menu that showed a move
 * the server never stored would be a lie), re-reads, and says the save failed (with the
 * server's reason for a 400). A login change bumps `generation`; a save still in flight
 * for the previous login then writes nothing into the new one's menu. */
import React from 'react';
import { ApiError, fetchNavLayout, saveNavLayout } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { VIEW_REGISTRY } from '@/lib/viewRegistry';
import { showToast } from '@/components/ui/toast';
import {
  NAV_GROUP_ORDER,
  NAV_GROUP_PAGES,
  STANDALONE_PATHS,
  groupOf,
} from '@/config/nav-groups';
import {
  isStandard,
  normalise,
  standardLayout,
  toWire,
  saveWithReconcile,
  type LayoutOp,
  type NavLayout,
} from '@/config/nav-layout';

export type { LayoutOp };

/** The standard menu's folders: the SPEC-452 verb groups, with any registry page that
 *  nav-groups.ts does not list appended to the fallback group (never hidden). */
const CONFIGURED = new Set(NAV_GROUP_ORDER.flatMap((label) => NAV_GROUP_PAGES[label]));
const REGISTRY_PATHS = VIEW_REGISTRY.map((p) => p.path);
export const STANDARD_GROUPS: ReadonlyArray<{ label: string; paths: string[] }> = NAV_GROUP_ORDER.map((label) => ({
  label,
  paths: [
    ...NAV_GROUP_PAGES[label],
    ...REGISTRY_PATHS.filter((p) => !STANDALONE_PATHS.includes(p) && !CONFIGURED.has(p) && groupOf(p) === label),
  ].filter((p) => REGISTRY_PATHS.includes(p)),
}));

interface Confirmed {
  layout: NavLayout;
  version: number;
  /** A stored row exists (false = the standard menu, never saved). */
  customised: boolean;
}

interface Pending {
  seq: number;
  op: LayoutOp;
}

export interface NavLayoutApi {
  /** False until the first GET has answered (or failed). */
  loaded: boolean;
  /** What the menu shows: confirmed + every pending op. */
  layout: NavLayout;
  /** True when the shown layout differs from the standard menu (ignoring favourites). */
  customised: boolean;
  /** Pages the access-tag hook withholds from this login ([] in v1). */
  locked: string[];
  /** Apply an op now and save it. */
  apply: (op: LayoutOp) => void;
  /** Back to the standard menu (keeps the favourites). */
  reset: () => void;
  arrangeOpen: boolean;
  openArrange: () => void;
  closeArrange: () => void;
}

const NavLayoutContext = React.createContext<NavLayoutApi | null>(null);

function standardConfirmed(favourites: string[] = []): Confirmed {
  return { layout: standardLayout(STANDARD_GROUPS, favourites), version: 0, customised: false };
}

export function NavLayoutProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const username = user?.username;

  const [confirmed, setConfirmed] = React.useState<Confirmed>(() => standardConfirmed());
  const [pending, setPending] = React.useState<Pending[]>([]);
  const [locked, setLocked] = React.useState<string[]>([]);
  const [loaded, setLoaded] = React.useState(false);
  const [arrangeOpen, setArrangeOpen] = React.useState(false);

  // Refs mirror state for the async flush, which must read the LATEST values. Every writer
  // (setBoth, apply) sets the ref and the state together, so the refs are never assigned
  // during render (review finding 16).
  const confirmedRef = React.useRef(confirmed);
  const pendingRef = React.useRef(pending);
  const saveChain = React.useRef<Promise<unknown>>(Promise.resolve());
  const saveSeq = React.useRef(0);
  const loadedPromise = React.useRef<Promise<void>>(Promise.resolve());
  // True once a GET has answered for THIS login. Until then the client does not know the
  // stored version or the legacy pins, so it must not write (review finding 1: a save built
  // on the default menu after a failed GET would bury the person's stars for good).
  const readOk = React.useRef(false);
  // Bumped when the login changes. An in-flight save for the previous login must not write
  // its answer into the new login's menu (review finding 7).
  const generation = React.useRef(0);

  const setBoth = (c: Confirmed, p: Pending[]) => {
    confirmedRef.current = c;
    pendingRef.current = p;
    setConfirmed(c);
    setPending(p);
  };

  const fromResponse = React.useCallback((raw: unknown, version: number, legacy?: string[]): Confirmed => {
    const layout = normalise(raw);
    return layout
      ? { layout, version, customised: true }
      : { ...standardConfirmed(legacy ?? []), version };
  }, []);

  const read = React.useCallback(async (): Promise<Confirmed> => {
    const res = await fetchNavLayout();
    setLocked(Array.isArray(res?.locked) ? res.locked : []);
    return fromResponse(res?.layout, Number(res?.version) || 0, res?.legacy_favourites);
  }, [fromResponse]);

  React.useEffect(() => {
    generation.current += 1;
    readOk.current = false;
    setLoaded(false);
    setBoth(standardConfirmed(), []);
    if (!username) return;
    let alive = true;
    loadedPromise.current = read()
      .then((c) => { if (alive) { readOk.current = true; setBoth(c, pendingRef.current); } })
      // The menu is an organisation nicety: a failed read shows the standard menu. A save
      // retries the read first (see flush) and never writes on top of an unread menu.
      .catch(() => undefined)
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [username, read]);

  const flush = React.useCallback(() => {
    saveChain.current = saveChain.current
      .catch(() => undefined)
      .then(() => loadedPromise.current.catch(() => undefined))
      .then(async () => {
        const gen = generation.current;
        const current = () => gen === generation.current;
        const batch = pendingRef.current;
        if (!batch.length) return;
        const top = batch[batch.length - 1].seq;
        const drop = (list: Pending[]) => list.filter((p) => p.seq > top);
        if (!readOk.current) {
          try {
            const c = await read();
            if (!current()) return;
            readOk.current = true;
            setBoth(c, pendingRef.current);
          } catch {
            if (!current()) return;
            setBoth(confirmedRef.current, drop(pendingRef.current));
            showToast('Your menu could not be loaded, so that change was not saved. Please try again.', 'error');
            return;
          }
        }

        // SPEC-563 fix loop 2 (Codex finding 3): the save and its 409 reconcile live in the pure
        // saveWithReconcile (config/nav-layout.ts, Node-tested). Each 409 hands back the server's
        // CURRENT layout and version; the next try is built on that one, at most MAX_RECONCILES
        // times. Whatever happens, the menu then shows the NEWEST server state that was seen.
        const outcome = await saveWithReconcile<Confirmed>({
          base: confirmedRef.current,
          ops: batch.map((p) => p.op),
          put: async (version, layout) => {
            const res = await saveNavLayout(version, toWire(layout));
            return fromResponse(res.layout, res.version);
          },
          conflictOf: (err) => {
            if (!(err instanceof ApiError) || err.status !== 409) return null;
            const body = (err.body ?? {}) as { layout?: unknown; version?: number };
            return fromResponse(body.layout, Number(body.version) || 0, confirmedRef.current.layout.favourites);
          },
          // No retry once the login has changed (the next PUT would carry this login's layout
          // under the next login's token).
          proceed: current,
        });
        if (!current() || outcome.kind === 'abandoned') return;
        const rest = drop(pendingRef.current);
        if (outcome.kind === 'saved') {
          setBoth(outcome.state, rest);
          return;
        }
        if (outcome.kind === 'no-longer-applies') {
          // Every queued change is a no-op on the newer menu (its folder was deleted in the
          // other tab, say). Say so rather than report a save that changed nothing.
          // Either the other tab already made the same change, or its newer menu makes this one
          // pointless (its folder was deleted there, say). The copy claims neither.
          setBoth(outcome.state, rest);
          showToast('Your menu changed in another tab. It now shows that version, with nothing left to save.', 'info');
          return;
        }
        if (outcome.kind === 'behind') {
          setBoth(outcome.state, rest);
          showToast('Your menu kept changing in another tab. It now shows the newest version; make your change again.', 'info');
          return;
        }
        // failed: a non-409 error. Show the newest server state if a 409 already told us it;
        // otherwise read it again.
        if (outcome.state) {
          setBoth(outcome.state, rest);
        } else {
          try {
            const c = await read();
            if (!current()) return;
            setBoth(c, rest);
          } catch {
            setBoth(confirmedRef.current, rest);
          }
        }
        // A 400 carries the reason in planner words ("Folder name is longer than 40
        // characters."); show it rather than a generic line (review finding 6).
        const err = outcome.error;
        showToast(
          err instanceof ApiError && err.status === 400 && err.detail
            ? `Your menu could not be saved: ${err.detail}`
            : 'Your menu could not be saved. Please try again.',
          'error',
        );
      });
  }, [fromResponse, read]);

  const apply = React.useCallback((op: LayoutOp) => {
    const next = [...pendingRef.current, { seq: ++saveSeq.current, op }];
    pendingRef.current = next;
    setPending(next);
    flush();
  }, [flush]);

  const layout = React.useMemo(
    () => pending.reduce((l, p) => p.op(l), confirmed.layout),
    [confirmed, pending],
  );

  const reset = React.useCallback(() => {
    apply((l) => standardLayout(STANDARD_GROUPS, l.favourites));
  }, [apply]);

  const value = React.useMemo<NavLayoutApi>(() => ({
    loaded,
    layout,
    customised: !isStandard(layout, STANDARD_GROUPS),
    locked,
    apply,
    reset,
    arrangeOpen,
    openArrange: () => setArrangeOpen(true),
    closeArrange: () => setArrangeOpen(false),
  }), [loaded, layout, locked, apply, reset, arrangeOpen]);

  return React.createElement(NavLayoutContext.Provider, { value }, children);
}

/** The shared layout. Outside a provider (a test harness, a page rendered alone) it returns
 *  a read-only standard menu, so the Sidebar never crashes for want of a provider. */
export function useNavLayout(): NavLayoutApi {
  const ctx = React.useContext(NavLayoutContext);
  return ctx ?? FALLBACK;
}

const FALLBACK: NavLayoutApi = {
  loaded: true,
  layout: standardLayout(STANDARD_GROUPS),
  customised: false,
  locked: [],
  apply: () => undefined,
  reset: () => undefined,
  arrangeOpen: false,
  openArrange: () => undefined,
  closeArrange: () => undefined,
};
